import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Divider,
  TablePagination
} from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Page from '../../../components/Page';
import Loader from '../../../components/Loader';
import { FaEye, FaPhoneAlt, FaEnvelope, FaWhatsapp } from 'react-icons/fa';
import { config } from '../../../utils/utils';
import { useSelector } from 'react-redux';

interface ClickBreakdown {
  call: number;
  whatsapp: number;
  email: number;
}

interface AdDetail {
  adId: string;
  title: string;
  status: string;
  totalViews: number;
  totalContactClicks: number;
  clickBreakdown: ClickBreakdown;
}

interface AgentPerformanceRow {
  agentId: string;
  agentName: string;
  email: string;
  phone: string;
  adsCreated: number;
  totalViews: number;
  totalContactClicks: number;
  phoneClicks: number;
  whatsappClicks: number;
  emailClicks: number;
  userRole?: string;
}

interface AdvertiserDetail {
  advertiserId: string;
  name: string;
  email: string;
  userRole?: string;
  isCompanyView?: boolean;
  totalAds: number;
  totalViews: number;
  totalContactClicks: number;
  clickBreakdown?: ClickBreakdown;
  agents?: AgentPerformanceRow[];
  ads: AdDetail[];
  total: number;
  limit: number;
  skip: number;
}

interface RootState {
  auth: {
    accessToken: string;
  };
}

const AdvertiserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AdvertiserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!accessToken) {
      navigate('/login', { state: { from: `/kpi/advertiser-detail/${id}` } });
    }
  }, [accessToken, navigate, id]);

  useEffect(() => {
    const fetchAdvertiserDetails = async () => {
      if (!accessToken) return;
      
      setLoading(true);
      try {
        if (!id) {
          throw new Error('Advertiser ID is required');
        }

        // Log the API URL for debugging
        const queryParams = new URLSearchParams();
        queryParams.append('$limit', rowsPerPage.toString());
        queryParams.append('$skip', (page * rowsPerPage).toString());
        if (startDate) {
          queryParams.append('startDate', startDate);
        }
        if (endDate) {
          queryParams.append('endDate', endDate);
        }

        const apiUrl = `${config.BASE_URL}/kpi/advertiser-ads-contact/${id}?${queryParams.toString()}`;
        console.log('API URL:', apiUrl);
        
        if (!config.BASE_URL) {
          throw new Error('API Base URL is not configured correctly');
        }

        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.status === 401) {
          // Handle unauthorized - redirect to login
          navigate('/login', { state: { from: `/kpi/advertiser-detail/${id}` } });
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch advertiser details');
        }

        const result = await response.json();
        setData(result);
        setError(null);
      } catch (err) {
        console.error('Error fetching advertiser details:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchAdvertiserDetails();
    }
  }, [id, accessToken, navigate, page, rowsPerPage, startDate, endDate]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'published':
        return 'success';
      case 'pending':
      case 'submitted':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Page title="Advertiser Details">
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Loader />
          </Box>
        </Container>
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page title="Advertiser Details">
        <Container>
          <Box sx={{ pt: 3, pb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4">Error</Typography>
            <Button
              onClick={handleGoBack}
              variant='outlined'
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </Box>
          <Typography color="error">{error || 'No data available'}</Typography>
        </Container>
      </Page>
    );
  }

  const getClickBreakdown = (): ClickBreakdown => {
    if (data.clickBreakdown) {
      return data.clickBreakdown;
    }
    return data.ads.reduce(
      (acc, ad) => ({
        call: acc.call + (ad.clickBreakdown?.call || 0),
        whatsapp: acc.whatsapp + (ad.clickBreakdown?.whatsapp || 0),
        email: acc.email + (ad.clickBreakdown?.email || 0),
      }),
      { call: 0, whatsapp: 0, email: 0 }
    );
  };

  const clickBreakdown = getClickBreakdown();

  return (
    <Page title={`Advertiser: ${data.name}`}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ pt: 3, pb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4">{data.name}</Typography>
            <Typography variant="body1" color="text.secondary">
              {data.email}
            </Typography>
          </Box>
          <Button
            onClick={handleGoBack}
            variant='outlined'
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', bgcolor: 'white' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Box sx={{ mb: 2, p: 1.5, borderRadius: '50%', bgcolor: '#1976d220', color: '#1976d2', display: 'flex' }}>
                  <FaEye size={20} />
                </Box>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'medium' }}>
                  {(data.totalViews || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                  Total Ad Views
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', bgcolor: 'white' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Box sx={{ mb: 2, p: 1.5, borderRadius: '50%', bgcolor: '#2e7d3220', color: '#2e7d32', display: 'flex' }}>
                  <FaPhoneAlt size={20} />
                </Box>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'medium' }}>
                  {(data.totalContactClicks || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                  Total Contact Clicks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', bgcolor: 'white' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
                  <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ bgcolor: '#e5737320', color: '#e57373', p: 1.5, borderRadius: '50%', display: 'flex', mb: 2 }}>
                      <FaPhoneAlt size={20} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
                      {clickBreakdown.call.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ bgcolor: '#4caf5020', color: '#4caf50', p: 1.5, borderRadius: '50%', display: 'flex', mb: 2 }}>
                      <FaWhatsapp size={20} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
                      {clickBreakdown.whatsapp.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ bgcolor: '#42a5f520', color: '#42a5f5', p: 1.5, borderRadius: '50%', display: 'flex', mb: 2 }}>
                      <FaEnvelope size={20} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
                      {clickBreakdown.email.toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                  Contact Click Breakdown
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Company agent breakdown or ads table */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {data.isCompanyView ? 'Agent Performance Breakdown' : 'Advertisement Performance'}
            </Typography>
            {data.isCompanyView ? (
              <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">S.No</TableCell>
                      <TableCell align="left">Name</TableCell>
                      <TableCell align="left">Email</TableCell>
                      <TableCell align="left">Phone</TableCell>
                      <TableCell align="center">Ads</TableCell>
                      <TableCell align="center">Ad Views</TableCell>
                      <TableCell align="center">Phone</TableCell>
                      <TableCell align="center">WhatsApp</TableCell>
                      <TableCell align="center">Email</TableCell>
                      <TableCell align="center">Total Contacts</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.agents || []).map((agent, index) => (
                      <TableRow key={agent.agentId} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                        <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell align="left">{agent.agentName}</TableCell>
                        <TableCell align="left">{agent.email}</TableCell>
                        <TableCell align="left">{agent.phone || '-'}</TableCell>
                        <TableCell align="center">{agent.adsCreated}</TableCell>
                        <TableCell align="center">{(agent.totalViews || 0).toLocaleString()}</TableCell>
                        <TableCell align="center">{agent.phoneClicks}</TableCell>
                        <TableCell align="center">{agent.whatsappClicks}</TableCell>
                        <TableCell align="center">{agent.emailClicks}</TableCell>
                        <TableCell align="center">{agent.totalContactClicks}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">S.No</TableCell>
                    <TableCell align="left">Ad Title</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Ad Views</TableCell>
                    <TableCell align="center">Contact Clicks</TableCell>
                    <TableCell align="center">Contact Click Breakdown</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.ads.map((ad, index) => (
                    <TableRow key={ad.adId} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell align="left" component="th" scope="row">
                        {ad.title}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={ad.status}
                          size="small"
                          color={getStatusColor(ad.status) as any}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">{(ad.totalViews || 0).toLocaleString()}</TableCell>
                      <TableCell align="center">{(ad.totalContactClicks || 0).toLocaleString()}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Call</Typography>
                            <Typography variant="body2">{ad.clickBreakdown?.call || 0}</Typography>
                          </Box>
                          <Divider orientation="vertical" flexItem />
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">WhatsApp</Typography>
                            <Typography variant="body2">{ad.clickBreakdown?.whatsapp || 0}</Typography>
                          </Box>
                          <Divider orientation="vertical" flexItem />
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Email</Typography>
                            <Typography variant="body2">{ad.clickBreakdown?.email || 0}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={data.total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default AdvertiserDetail; 